import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useUniqueId } from '../../hooks';
import { VisuallyHidden } from '../VisuallyHidden';
import { modalDefaultProps } from './props';
import * as S from './styles';
const CloseIcon = () => (React.createElement("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M13.0607 12L18.5303 17.4697C18.8232 17.7626 18.8232 18.2374 18.5303 18.5303C18.2374 18.8232 17.7626 18.8232 17.4697 18.5303L12 13.0607L6.53033 18.5303C6.23744 18.8232 5.76256 18.8232 5.46967 18.5303C5.17678 18.2374 5.17678 17.7626 5.46967 17.4697L10.9393 12L5.46967 6.53033C5.17678 6.23744 5.17678 5.76256 5.46967 5.46967C5.76256 5.17678 6.23744 5.17678 6.53033 5.46967L12 10.9393L17.4697 5.46967C17.7626 5.17678 18.2374 5.17678 18.5303 5.46967C18.8232 5.76256 18.8232 6.23744 18.5303 6.53033L13.0607 12Z", fill: "#4A4A4A" })));
/**
 * Componente Modal do Design System OLX
 */
export const Modal = ({ children, open = modalDefaultProps.open, preventCloseOnOutsideClick = modalDefaultProps.preventCloseOnOutsideClick, size = modalDefaultProps.size, maxHeight = modalDefaultProps.maxHeight, maxWidth = modalDefaultProps.maxWidth, closeCallback, onAfterOpen, }) => {
    const [showModal, setShowModal] = useState(open);
    const modalRef = useRef(null);
    const modalContentID = useUniqueId('ds-modal-content');
    const closeModal = useCallback(() => {
        setShowModal(false);
        closeCallback && closeCallback();
    }, [closeCallback]);
    useEffect(() => {
        setShowModal(open);
        if (open && onAfterOpen)
            onAfterOpen();
    }, [onAfterOpen, open]);
    useEffect(() => {
        const handleKeyboardNavigation = (event) => {
            var _a;
            if (showModal && event.key === 'Escape') {
                return closeModal();
            }
            const elementsToNavigate = (_a = modalRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('a, button:not([disabled]), input:not([readonly]), details, select, textarea, [tabindex]:not([tabindex^="-"])');
            if (event.key === 'Tab' || event.shiftKey) {
                const firstElement = elementsToNavigate.item(0);
                const lastElement = elementsToNavigate.item(elementsToNavigate.length - 1);
                if (!event.shiftKey && event.key === 'Tab' && event.target === lastElement) {
                    event.preventDefault();
                    firstElement.focus();
                }
                if (event.shiftKey && event.key === 'Tab' && event.target === firstElement) {
                    event.preventDefault();
                    lastElement.focus();
                }
            }
        };
        window.addEventListener('keydown', handleKeyboardNavigation);
        return () => {
            window.removeEventListener('keydown', handleKeyboardNavigation);
        };
    }, [closeModal, showModal]);
    useEffect(() => {
        if (preventCloseOnOutsideClick) {
            return;
        }
        const handleClickOutside = (event) => {
            if (showModal && modalRef.current && !modalRef.current.contains(event.target)) {
                closeModal();
            }
        };
        window.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('mousedown', handleClickOutside);
        };
    }, [closeModal, preventCloseOnOutsideClick, showModal]);
    return (React.createElement(S.ModalWrapper, { id: "modal", "data-ds-component": "DS-Modal", className: showModal ? 'show' : '', size: size, "data-testid": "modal-overlay", "aria-hidden": !showModal },
        React.createElement(S.Modal, { role: "dialog", "aria-modal": "true", "aria-labelledby": modalContentID, "data-testid": "modal", size: size, maxHeight: maxHeight, maxWidth: maxWidth, ref: modalRef, className: showModal ? 'show' : '' },
            React.createElement(S.IconContainer, null,
                React.createElement(S.ButtonWrapper, { "data-ds-component": "DS-Modal-Button", "data-testid": "closeButton", onClick: closeModal, type: "button" },
                    React.createElement(VisuallyHidden, null, "Fechar janela de di\u00E1logo"),
                    React.createElement(CloseIcon, null))),
            React.createElement(S.FooterContainer, { id: modalContentID }, children))));
};
